/**
 * Jeu d'icônes maison, au trait, dessinées en 24 × 24.
 *
 * Pourquoi pas des emoji : ils imposent leurs propres couleurs, ignorent la
 * palette Material et changent d'aspect selon le système. Ces icônes héritent
 * de `currentColor` et suivent donc le thème, y compris en mode sombre.
 *
 * Aucune police d'icônes, aucun appel réseau : le tracé est dans le document.
 */

import { html, svg, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

export type IconName =
  | 'ecole'
  | 'vote'
  | 'affichage'
  | 'chevron'
  | 'retour'
  | 'plus'
  | 'imprimante'
  | 'telecharger'
  | 'verrou'
  | 'check'
  | 'checkCercle'
  | 'personnes'
  | 'liste'
  | 'crayon'
  | 'balance'
  | 'info'
  | 'alerte'
  | 'soleil'
  | 'lune'
  | 'auto'
  | 'horloge'
  | 'poubelle';

/** Un point plein, pour les icônes qui ont besoin d'un aplat. */
const pointPlein = (cx: number, cy: number, r: number): TemplateResult =>
  svg`<circle cx=${cx} cy=${cy} r=${r} fill="currentColor" stroke="none"></circle>`;

const DESSINS: Record<IconName, TemplateResult> = {
  ecole: svg`
    <path d="M3 9l9-5 9 5-9 5-9-5z"></path>
    <path d="M7 11.5V16c0 1.5 2.2 2.5 5 2.5s5-1 5-2.5v-4.5"></path>`,
  vote: svg`
    <path d="M4 13h16v6.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V13z"></path>
    <path d="M8 13V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v7"></path>
    <path d="M9.5 8.6l1.8 1.8 3.3-3.3"></path>`,
  affichage: svg`
    <rect x="2.5" y="4" width="19" height="12.5" rx="2"></rect>
    <path d="M8.5 20.5h7"></path>
    <path d="M12 16.5v4"></path>`,
  chevron: svg`<path d="M9.5 5.5l6.5 6.5-6.5 6.5"></path>`,
  retour: svg`
    <path d="M19.5 12H5"></path>
    <path d="M11.5 5.5L5 12l6.5 6.5"></path>`,
  plus: svg`
    <path d="M12 5v14"></path>
    <path d="M5 12h14"></path>`,
  imprimante: svg`
    <path d="M7 8V3.5h10V8"></path>
    <rect x="3.5" y="8" width="17" height="8" rx="2"></rect>
    <path d="M7 13h10v7.5H7z"></path>`,
  telecharger: svg`
    <path d="M12 3.5v11.5"></path>
    <path d="M7.5 10.5L12 15l4.5-4.5"></path>
    <path d="M4.5 20.5h15"></path>`,
  verrou: svg`
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2"></rect>
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"></path>`,
  check: svg`<path d="M4.5 12.5l5 5 10-11"></path>`,
  checkCercle: svg`
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M8 12.4l2.9 2.9L16.4 9.4"></path>`,
  personnes: svg`
    <circle cx="9" cy="8" r="3.2"></circle>
    <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5"></path>
    <path d="M16 5.6a3.2 3.2 0 0 1 0 6.1"></path>
    <path d="M17.2 15.3c2.1.6 3.3 2.2 3.3 4.2"></path>`,
  liste: svg`
    <path d="M8.5 6.5h11.5"></path>
    <path d="M8.5 12h11.5"></path>
    <path d="M8.5 17.5h11.5"></path>
    ${pointPlein(4.2, 6.5, 1.15)}
    ${pointPlein(4.2, 12, 1.15)}
    ${pointPlein(4.2, 17.5, 1.15)}`,
  crayon: svg`
    <path d="M4 20h4L19.5 8.5l-4-4L4 16v4z"></path>
    <path d="M14.5 5.5l4 4"></path>`,
  balance: svg`
    <path d="M4 20.5h9"></path>
    <path d="M13.5 3.8l6.7 6.7-2.8 2.8-6.7-6.7z"></path>
    <path d="M11.2 6.1l-7 7 4.3 4.3 7-7"></path>`,
  info: svg`
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M12 11.2v5.3"></path>
    ${pointPlein(12, 7.9, 1.1)}`,
  alerte: svg`
    <path d="M12 3.8L2.6 20.2h18.8L12 3.8z"></path>
    <path d="M12 9.6v5"></path>
    ${pointPlein(12, 17.5, 1.05)}`,
  soleil: svg`
    <circle cx="12" cy="12" r="4"></circle>
    <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6"></path>`,
  lune: svg`<path d="M20.2 14.6A8.6 8.6 0 0 1 9.4 3.8a8.6 8.6 0 1 0 10.8 10.8z"></path>`,
  auto: svg`
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M12 3v18"></path>
    <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"></path>`,
  horloge: svg`
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M12 7v5.4l3.6 2.1"></path>`,
  poubelle: svg`
    <path d="M4.5 7h15"></path>
    <path d="M9.5 7V4.5h5V7"></path>
    <path d="M6.5 7l1 13.5h9L17.5 7"></path>`,
};

/**
 * Rend une icône. `taille` est exprimée en pixels.
 *
 * `emplacement` sert aux boutons Material Web, qui réservent un slot nommé
 * pour l'icône de tête : sans lui, le tracé tombe dans le slot du libellé et
 * s'empile au-dessus du texte.
 */
export function icone(nom: IconName, taille = 24, emplacement?: 'icon'): TemplateResult {
  return html`<svg
    class="icone"
    slot=${ifDefined(emplacement)}
    viewBox="0 0 24 24"
    width=${taille}
    height=${taille}
    fill="none"
    stroke="currentColor"
    stroke-width="1.9"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    ${DESSINS[nom]}
  </svg>`;
}
